/**
 * Which part of the product brought someone to sign up? Practice, Analyze, a sport page, pricing...
 *
 * Attribution (lib/attribution.js) already says where a visitor FIRST landed. That is not the same as what made them sign up: someone can land on a
 * blog post, wander to /practice, tap Start and be sent to /auth. This remembers the page they came from when they reached /auth (or the page they were
 * trying to use: the "after login" path Practice stores), so each signup can be counted as "practice", "analyze", "sport_page", "pricing", ...
 *
 *   rememberPrevPath(path)   called on every navigation except /auth (IntentWatcher)
 *   snapshotIntent()         called when /auth opens: freezes the best answer
 *   getSignupIntent()        the answer: { feature, detail, source }  source = after_login | page | landing | unknown
 *
 * Sign-ups that happen on the page itself (the Analyze upload step, the quick sign-up card) are classified by trackSignup() in lib/analytics.js.
 * Never blocks anything: private mode just means "unknown".
 */
const KEY = "formanti_signup_intent";
const PREV = "formanti_prev_path";
const FIRST = "formanti_first_touch";
const PREV_TTL_MS = 30 * 60 * 1000;
const KEEP_MS = 3 * 60 * 60 * 1000;

const SPORT_PAGES = new Set(["badminton", "tennis", "table-tennis", "pickleball", "cricket", "swimming", "football", "basketball", "gym", "weight-lifting", "physiotherapy"]);
const APP_PAGES = new Set(["dashboard", "progress", "training", "profile", "wallet", "community", "card", "assessment", "referral"]);

/** "/practice?sport=badminton&shot=smash" -> { feature: "practice", detail: "badminton/smash" }. */
export function classifyPath(path) {
  const [pathname = "/", query = ""] = String(path || "/").split("?");
  const q = new URLSearchParams(query);
  const seg = pathname.split("/").filter(Boolean);
  const first = seg[0] || "";
  if (first === "practice") return { feature: "practice", detail: [q.get("sport"), q.get("shot")].filter(Boolean).join("/") || seg[1] || "" };
  if (first === "analyze" || first === "demo") return { feature: "analyze", detail: "" };
  if (first === "pricing") return { feature: "pricing", detail: "" };
  if (SPORT_PAGES.has(first)) return { feature: "sport_page", detail: seg[1] ? `${first}/${seg[1]}` : first };
  if (first === "gear" || first === "marketplace") return { feature: "gear", detail: "" };
  if (first === "blog") return { feature: "blog", detail: seg[1] || "" };
  if (APP_PAGES.has(first)) return { feature: "app", detail: first };
  if (!first) return { feature: "home", detail: "" };
  return { feature: "other", detail: first };
}

// the path Practice (and the paywall) remember before sending someone to /auth: the same key and 30-minute window as lib/practiceAccess.js (not imported: that file pulls in the whole app)
const AFTER_KEY = "formanti_after_login";
const AFTER_TTL_MS = 30 * 60 * 1000;
function peekAfterLogin() {
  try {
    const { path, at } = JSON.parse(localStorage.getItem(AFTER_KEY) || "null") || {};
    if (typeof path === "string" && path.startsWith("/") && !path.startsWith("//") && Date.now() - at < AFTER_TTL_MS) return { path, at };
  } catch { /* ignore */ }
  return null;
}

const read = (key) => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };

export function rememberPrevPath(path) {
  try { localStorage.setItem(PREV, JSON.stringify({ path: String(path).slice(0, 200), at: Date.now() })); } catch { /* private mode */ }
}

/** The person has just reached /auth: freeze what brought them. Keeps an earlier answer when nothing better is known (a verify link opened in a new tab). */
export function snapshotIntent() {
  try {
    const after = peekAfterLogin();
    const prev = read(PREV);
    let found = null;
    const prevFresh = prev && Date.now() - prev.at < PREV_TTL_MS ? prev : null;
    // the page they were trying to use (Practice's Start) is stored just before /auth opens, so it is newer than the page visit; an older one is a leftover from earlier
    if (after && (!prevFresh || after.at >= prevFresh.at)) found = { ...classifyPath(after.path), source: "after_login" };
    else if (prevFresh) found = { ...classifyPath(prevFresh.path), source: "page" };
    if (found) localStorage.setItem(KEY, JSON.stringify({ ...found, at: Date.now() }));
  } catch { /* nothing lost but the answer */ }
}

export function getSignupIntent() {
  const kept = read(KEY);
  if (kept && kept.feature && Date.now() - kept.at < KEEP_MS) return { feature: kept.feature, detail: kept.detail || "", source: kept.source || "page" };
  const first = read(FIRST);
  if (first && first.landing) return { ...classifyPath(first.landing), source: "landing" };
  return { feature: "unknown", detail: "", source: "unknown" };
}
